import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { bmoApi } from '../services/api';
import { colors, getScoreColor, KK_THEMES, KAYAKALP_PASS_SCORE } from '../theme';
import { Card, ScoreBadge, ProgressBar, SectionHeader, Spinner, AlertBanner } from '../components/common';

export default function FacilityDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    Promise.all([
      bmoApi.getFacilityDetail(id),
      bmoApi.getThemeBreakdown(id),
      bmoApi.getScoreTrend(id, { months: 6 }),
    ])
      .then(([detail, themes, trend]) => setData({ ...detail, themes: themes?.breakdown || [], trend: trend?.history || [] }))
      .catch((err) => setError(err?.message || 'Could not load facility.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const facility = data;
  const pct      = facility?.score_percentage ?? 0;
  const passing  = pct >= KAYAKALP_PASS_SCORE;

  // Radar data
  const radarData = KK_THEMES.map((t) => {
    const td = (facility?.themes || []).find((x) => x.theme_id === t.id);
    return { theme: t.code, fullMark:100, score: td?.percentage ?? 0 };
  });

  return (
    <div>
      {/* Back + header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background:'none', border:`1px solid ${colors.neutral[300]}`, borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:13, color: colors.text.secondary }}
        >
          ← Back
        </button>
        <div>
          <h1 style={{ margin:'0 0 2px', fontSize:22, fontWeight:700, color: colors.text.primary }}>{facility?.name}</h1>
          <p style={{ margin:0, fontSize:13, color: colors.text.secondary }}>{facility?.type_label} · {facility?.block} Block</p>
        </div>
      </div>

      {/* Score hero */}
      <Card style={{ marginBottom:16, display:'flex', alignItems:'center', gap:32, flexWrap:'wrap' }}>
        <div style={{ textAlign:'center' }}>
          <ScoreBadge pct={pct} size="lg" />
        </div>
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:16 }}>
          {[
            { label:'Facility Type', value: facility?.type_label || '—' },
            { label:'MO In-Charge',  value: facility?.mo_name || '—' },
            { label:'Last Updated',  value: facility?.last_updated || '—' },
            { label:'Pass Mark',     value: `${KAYAKALP_PASS_SCORE}%`, color: passing ? colors.success[700] : colors.error[600] },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize:11, color: colors.text.secondary, marginBottom:2 }}>{item.label}</div>
              <div style={{ fontSize:14, fontWeight:600, color: item.color || colors.text.primary }}>{item.value}</div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

        {/* Radar chart */}
        <Card>
          <SectionHeader title="Theme Radar" subtitle="Score across all 8 Kayakalp themes" />
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="theme" tick={{ fontSize:12 }} />
              <Radar name="Score" dataKey="score" stroke={colors.primary[600]} fill={colors.primary[600]} fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </Card>

        {/* Score trend */}
        <Card>
          <SectionHeader title="Score Trend" subtitle="Last 6 months" />
          {(facility?.trend || []).length === 0
            ? <div style={{ padding:32, textAlign:'center', color: colors.text.secondary, fontSize:14 }}>No trend data yet</div>
            : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={facility.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="month" tick={{ fontSize:12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize:12 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(1)}%`]} />
                  <Line type="monotone" dataKey="percentage" stroke={colors.primary[600]} strokeWidth={2} dot={{ r:4 }} />
                </LineChart>
              </ResponsiveContainer>
            )
          }
        </Card>
      </div>

      {/* Theme breakdown table */}
      <Card style={{ marginBottom:16 }}>
        <SectionHeader title="Score by Theme" subtitle="Kayakalp 2024 assessment tool — all 8 themes" />
        <div style={{ display:'grid', gap:10 }}>
          {KK_THEMES.map((t) => {
            const td    = (facility?.themes || []).find((x) => x.theme_id === t.id);
            const tPct  = td?.percentage ?? 0;
            const tColor = getScoreColor(tPct);
            return (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:28, height:28, borderRadius:14, background: colors.primary[50], display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color: colors.primary[700], flexShrink:0 }}>
                  {t.code}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color: colors.text.primary, marginBottom:4 }}>{t.label}</div>
                  <ProgressBar pct={tPct} height={6} showLabel />
                </div>
                <div style={{ fontSize:13, fontWeight:700, color: tColor, minWidth:42, textAlign:'right' }}>{Math.round(tPct)}%</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Certification & BMW quick links */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
        {[
          { icon:'🎖️', label:'Certifications', path:`/certifications?facility=${id}` },
          { icon:'🗑️', label:'BMW Waste Log',   path:`/bmw?facility=${id}` },
          { icon:'💉', label:'Vaccination D8.4',path:`/vaccination?facility=${id}` },
          { icon:'📊', label:'Full Report',      path:`/reports?facility=${id}` },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', background: colors.white, border:`1px solid ${colors.divider}`, borderRadius:8, cursor:'pointer', fontSize:14, color: colors.text.primary, fontWeight:500 }}
          >
            <span style={{ fontSize:20 }}>{item.icon}</span>{item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
